import { z } from 'zod';

import { captionStyleSchema } from './caption-styles';
import { IMAGE_MODEL_IDS } from './images';
import { HOOK_ANGLES } from './nodes/generate';
import { type DataType, PLATFORMS } from './types';

/**
 * What an item of each data type carries. Items are stored as JSON, validated
 * with these on the way in; media stays in R2 and items point at it.
 */

/** A post to learn from: an upload in the library, or (later) a link or a competitor's reel. */
/** The collection a starter read, and when - its matches can change after. */
const fromCollectionSchema = z.object({ id: z.uuid(), resolvedAt: z.string() });

export const sourcePayloadSchema = z.object({
  /** Stable identity for Cooldown: `asset:<id>` or `url:<url>`. */
  key: z.string(),
  mediaAssetId: z.uuid().optional(),
  collection: fromCollectionSchema.optional(),
  url: z.string().optional(),
  title: z.string(),
  caption: z.string().optional(),
  transcript: z.string().optional(),
  platforms: z.array(z.enum(PLATFORMS)).default([]),
  durationSeconds: z.number().optional(),
  postedAt: z.string().optional(),
  metrics: z
    .object({
      views: z.number().optional(),
      avgWatchSeconds: z.number().optional(),
      engagementRate: z.number().optional(),
      saves: z.number().optional(),
      /** Views against the poster's own average. */
      lift: z.number().optional(),
    })
    .default({}),
});

/** A post broken down: what makes it work. */
export const blueprintPayloadSchema = z.object({
  sourceTitle: z.string(),
  hook: z.string(),
  scenes: z.array(z.string()),
  pacing: z.string(),
  onScreenText: z.array(z.string()),
  format: z.string(),
  transcript: z.string(),
});

/** A script in the brand's voice. Hook variations set `angle`. */
export const scriptPayloadSchema = z.object({
  title: z.string(),
  hook: z.string(),
  body: z.string(),
  cta: z.string().optional(),
  angle: z.enum(HOOK_ANGLES).optional(),
  /** Approved claims the script relies on. */
  claims: z.array(z.string()).default([]),
});

/** A spoken word and when it's said, in seconds from the start of the video. */
export const spokenWordSchema = z.object({
  text: z.string(),
  start: z.number(),
  end: z.number(),
});

/**
 * A video segment: from the library (`mediaAssetId`) or cut by a flow
 * (`fileKey` only). `role` says which part of a reel it is.
 */
export const clipPayloadSchema = z.object({
  mediaAssetId: z.uuid().optional(),
  collection: fromCollectionSchema.optional(),
  fileKey: z.string().optional(),
  title: z.string(),
  kind: z.string(),
  role: z.enum(['hook', 'body', 'broll']).optional(),
  durationSeconds: z.number().optional(),
  /** The script it was made from, and its voiceover's timings. */
  script: scriptPayloadSchema.optional(),
  words: z.array(spokenWordSchema).optional(),
});

/**
 * How a generated image or video was made, to show and reproduce: every
 * prompt exactly as sent, and the settings.
 */
export const generationSchema = z.object({
  kind: z.enum(['sceneReplica', 'sceneVideo', 'personaVariation']),
  /** The first frame's redraw prompt. */
  framePrompt: z.string().optional(),
  /** How the video moves. */
  prompt: z.string().optional(),
  /** The persona shot's prompt, when a video animates one. */
  imagePrompt: z.string().optional(),
  imageModel: z.string().optional(),
  videoModel: z.string().optional(),
  format: z.string().optional(),
  quality: z.string().optional(),
  durationSeconds: z.number().optional(),
  /** The reel scene it remakes. */
  source: z.object({ assetId: z.uuid(), scene: z.int() }).optional(),
  personaId: z.uuid().optional(),
});
export type Generation = z.output<typeof generationSchema>;

/** A rendered video in R2. Edits replace the file and keep the rest. */
export const reelPayloadSchema = z.object({
  title: z.string(),
  fileKey: z.string(),
  durationSeconds: z.number(),
  width: z.int(),
  height: z.int(),
  script: scriptPayloadSchema.optional(),
  /** Voiceover timings - Subtitles need them; edits that move time keep them in step or drop them. */
  words: z.array(spokenWordSchema).optional(),
  caption: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  /** A short title for TikTok's and YouTube's own title field (Captions & hashtags). */
  postTitle: z.string().optional(),
  /** Library audio mixed in by Sound, for Platform policy's music check. */
  soundAssetId: z.uuid().optional(),
  /** Set by Schedule & publish: when it goes out (ISO). */
  scheduledAt: z.string().optional(),
  /** The review edit this file was rendered from; absent for the reel as the flow made it. */
  editId: z.uuid().optional(),
  /** The persona it shows (Scene video from Persona variations). */
  personaId: z.uuid().optional(),
  /** How it was made, when generated (Scene video). */
  generation: generationSchema.optional(),
});

/** Slides drawn as images in R2. */
export const carouselPayloadSchema = z.object({
  title: z.string(),
  slides: z
    .array(
      z.object({
        fileKey: z.string(),
        text: z.string(),
        /** The caption with its *highlight* marks, when it has any (text has them removed). */
        textMarkup: z.string().optional(),
        /** Carousel from slots: the slot and the library picks it was made from. */
        slot: z.string().optional(),
        imageAssetId: z.string().optional(),
        captionAssetId: z.string().optional(),
        /** Where the slot picks from and how its text is drawn - for rerolls in review. */
        pool: z
          .object({
            /** Unset when the images came from the step before (Build carousels). */
            images: z.string().optional(),
            captions: z.string().optional(),
            position: z.enum(['top', 'center', 'bottom']),
            /** The style it was drawn with (older items: one of the first three looks, by name). */
            style: z.union([
              z.enum(['tiktokWhite', 'tiktokBlack', 'outlined']),
              captionStyleSchema,
            ]),
            /** The preset or custom style picked, when the style was resolved from one. */
            styleRef: z.string().optional(),
          })
          .optional(),
        /** An AI background: what it was asked for and what made it - for AI rerolls. */
        image: z
          .object({
            prompt: z.string(),
            model: z.enum(IMAGE_MODEL_IDS),
            gateway: z.string(),
            assetId: z.string().optional(),
            /** The persona it shows, whose reference look it followed - rerolls follow it again. */
            personaId: z.string().optional(),
          })
          .optional(),
      }),
    )
    .min(1),
  width: z.int(),
  height: z.int(),
  script: scriptPayloadSchema.optional(),
  caption: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  /** A short title for TikTok's and YouTube's own title field (Captions & hashtags). */
  postTitle: z.string().optional(),
  scheduledAt: z.string().optional(),
  /** The persona it shows (Build carousels), for Schedule & publish. */
  personaId: z.uuid().optional(),
});

export type SourcePayload = z.output<typeof sourcePayloadSchema>;
export type BlueprintPayload = z.output<typeof blueprintPayloadSchema>;
export type ScriptPayload = z.output<typeof scriptPayloadSchema>;
export type ClipPayload = z.output<typeof clipPayloadSchema>;
/** One still image in R2 - e.g. a persona shot to keep or build carousels from. */
export const imagePayloadSchema = z.object({
  title: z.string(),
  fileKey: z.string(),
  contentType: z.string().optional(),
  width: z.int().optional(),
  height: z.int().optional(),
  /** The library image it is, when it came from (or was saved to) the library. */
  mediaAssetId: z.uuid().optional(),
  /** The persona it shows. */
  personaId: z.uuid().optional(),
  /** The reel scene it's the frame of (Reel scenes), to remake as a video. */
  scene: z
    .object({
      assetId: z.uuid(),
      scene: z.int(),
      context: z.string().optional(),
      text: z.string().optional(),
    })
    .optional(),
  /** What made it, when it was generated. */
  image: z
    .object({ prompt: z.string(), model: z.enum(IMAGE_MODEL_IDS), gateway: z.string() })
    .optional(),
  caption: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  /** A short title for TikTok's and YouTube's own title field (Captions & hashtags). */
  postTitle: z.string().optional(),
  /** How it was made, when generated (Persona variations). */
  generation: generationSchema.optional(),
});

export type ReelPayload = z.output<typeof reelPayloadSchema>;
export type ImagePayload = z.output<typeof imagePayloadSchema>;
export type CarouselPayload = z.output<typeof carouselPayloadSchema>;
export type SpokenWord = z.output<typeof spokenWordSchema>;

/** Payload schemas by data type. */
export const PAYLOAD_SCHEMAS: Record<DataType, z.ZodType> = {
  source: sourcePayloadSchema,
  blueprint: blueprintPayloadSchema,
  script: scriptPayloadSchema,
  clip: clipPayloadSchema,
  reel: reelPayloadSchema,
  carousel: carouselPayloadSchema,
  image: imagePayloadSchema,
};

/** A one-line summary of an item, for lists and the review queue. */
export function describeItem(dataType: DataType, payload: unknown): string {
  const p = payload as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  switch (dataType) {
    case 'script':
      return text(p.hook) || text(p.title);
    case 'blueprint':
      return text(p.hook) || text(p.sourceTitle);
    default:
      return text(p.title) || text(p.caption);
  }
}
